import { useCallback, useEffect, useRef } from 'react';
import { DeliveryPriceResult, DateOnly } from '../app/services/api/ofdClient';
import useNswagClient from './api/useNswagClient';

interface UseDeliveryPricingParams {
  retailerId?: number;
  storeNumber?: string;
}

export const normalizeDeliveryDate = (value: string): string | null => {
  // Accept yyyy-MM-dd directly
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  // Accept dd-MM-yyyy or dd/MM/yyyy and convert to yyyy-MM-dd
  const match = value.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (match) {
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    }
  }

  return null;
};

export const getWholePalletCount = (
  totalPalletsNeeded: number,
  containerFillPercents: number[],
): number => {
  const lastPalletFillPercent = containerFillPercents.at(-1) ?? 100;
  const hasPartialPallet = lastPalletFillPercent > 0 && lastPalletFillPercent < 100;

  return Math.max(0, Math.floor(totalPalletsNeeded) - (hasPartialPallet ? 1 : 0));
};

/**
 * Hook to resolve delivery pricing via the pricing engine.
 * Falls back gracefully — returns null if engine cannot resolve.
 */
const useDeliveryPricing = ({ retailerId, storeNumber }: UseDeliveryPricingParams) => {
  const resultCacheRef = useRef<Map<string, DeliveryPriceResult | null>>(new Map());
  const inFlightRef = useRef<Map<string, Promise<DeliveryPriceResult | null>>>(new Map());
  const { deliveryPricingClient } = useNswagClient();
  const deliveryPricingClientRef = useRef(deliveryPricingClient);

  useEffect(() => {
    deliveryPricingClientRef.current = deliveryPricingClient;
  }, [deliveryPricingClient]);

  useEffect(() => {
    // Pricing scope changed; invalidate per-key cache.
    resultCacheRef.current.clear();
    inFlightRef.current.clear();
  }, [retailerId, storeNumber]);

  const resolveDeliveryCharge = useCallback(async (
    shippingMethodId: number,
    totalPalletsNeeded: number,
    containerFillPercents: number[],
    deliveryDate: string,
    baseCharge?: number,
  ): Promise<DeliveryPriceResult | null> => {
    if (!shippingMethodId || totalPalletsNeeded <= 0) return null;

    const normalizedDeliveryDate = normalizeDeliveryDate(deliveryDate);
    if (!normalizedDeliveryDate) return null;

    const wholePallets = getWholePalletCount(totalPalletsNeeded, containerFillPercents);
    const lastPalletFillPercent = containerFillPercents.length > 0
      ? containerFillPercents[containerFillPercents.length - 1]
      : 100;

    // Deduplicate identical requests — return cached/in-flight result
    const key = `${shippingMethodId}-${totalPalletsNeeded}-${wholePallets}-${lastPalletFillPercent}-${normalizedDeliveryDate}-${retailerId ?? storeNumber ?? 'global'}-${baseCharge}`;
    if (resultCacheRef.current.has(key)) {
      return resultCacheRef.current.get(key) ?? null;
    }

    const inFlight = inFlightRef.current.get(key);
    if (inFlight) {
      return inFlight;
    }

    const requestPromise = (async () => {
      try {
        const result = await deliveryPricingClientRef.current.resolve({
          shippingMethodId,
          retailerId,
          wholePallets,
          lastPalletFillPercent,
          deliveryDate: normalizedDeliveryDate as unknown as DateOnly,
          baseCharge,
        });

        const resolved = result.resolved ? result : null;
        resultCacheRef.current.set(key, resolved);
        return resolved;
      } catch {
        // Silent fallback — pricing engine unavailable, use flat rate
        resultCacheRef.current.set(key, null);
        return null;
      } finally {
        inFlightRef.current.delete(key);
      }
    })();

    inFlightRef.current.set(key, requestPromise);
    return requestPromise;
  }, [retailerId, storeNumber]);

  return { resolveDeliveryCharge };
};

export default useDeliveryPricing;
